import React from 'react';
import { useDrop } from 'react-dnd';
import TaskRow, { TASK_DRAG_TYPE, type TaskDragItem } from './TaskRow';
import type { PlanGroup, PlanTask, Priority } from '../../../types/plan';

// 分组键。'none' 不是 priority 的取值，是「未标」这一档在分组里的键 ——
// 与服务端 PlanQuery::PRIORITY_LABELS 一一对应。
type PriorityKey = 'p1' | 'p2' | 'p3' | 'p4' | 'none';

const HINTS: Record<PriorityKey, string> = {
  p1: '最高',
  p2: '',
  p3: '',
  p4: '最低',
  none: '还没判过 —— 拖回这里等于取消优先级',
};

// 与 /todo 的 todo.p1..p4 token 同色。两处不共用常量（那边是 Tailwind 类名、
// 这边是内联色值给 borderLeftColor），但色值必须一致 —— 同一档优先级在两个
// 页面上是两种颜色的话，用户会以为它们是两回事。
const BORDER_COLORS: Record<PriorityKey, string> = {
  p1: '#d4380d',
  p2: '#d48806',
  p3: '#1677ff',
  p4: '#8b8880',
  none: '#d9d9d9',
};

type PriorityGridProps = {
  groups: PlanGroup[];
  onDropTask: (taskId: number, patch: { priority: Priority }) => void;
  onQuickSchedule: (task: PlanTask, when: 'today' | 'tomorrow') => void;
  onToggle: (task: PlanTask) => void;
  onEdit: (task: PlanTask) => void;
};

type CellProps = {
  group: PlanGroup;
  onDropTask: PriorityGridProps['onDropTask'];
  onQuickSchedule: PriorityGridProps['onQuickSchedule'];
  onToggle: (task: PlanTask) => void;
  onEdit: (task: PlanTask) => void;
};

const PriorityCell: React.FC<CellProps> = ({ group, onDropTask, onQuickSchedule, onToggle, onEdit }) => {
  const key = group.key as PriorityKey;
  // 'none' 格写回的是 null —— 这一格是「取消优先级」的唯一入口。
  const next: Priority = key === 'none' ? null : key;

  const [{ isOver }, drop] = useDrop<TaskDragItem, unknown, { isOver: boolean }>(
    () => ({
      accept: TASK_DRAG_TYPE,
      drop: (item) => {
        if (item.priority === next) return;
        onDropTask(item.id, { priority: next });
      },
      collect: (monitor) => ({ isOver: monitor.isOver() }),
    }),
    [key, onDropTask]
  );

  return (
    <div
      ref={drop as unknown as React.Ref<HTMLDivElement>}
      className={`flex min-h-[200px] flex-col rounded-xl border-l-4 border-y border-r p-3 transition-colors ${
        isOver ? 'bg-blue-50/40' : 'bg-gray-50/60'
      }`}
      style={{ borderLeftColor: BORDER_COLORS[key] }}
    >
      <div className="mb-3 flex items-baseline justify-between">
        <div>
          <div className="text-sm font-semibold" style={{ color: BORDER_COLORS[key] }}>
            {group.label}
          </div>
          {HINTS[key] && <div className="text-xs text-gray-400">{HINTS[key]}</div>}
        </div>
        <span className="text-xs text-gray-500">{group.stats.total} 项</span>
      </div>

      <div className="flex-1">
        {group.tasks.map((task) => (
          <TaskRow
            key={task.id}
            task={task}
            draggable
            showQuickSchedule
            onQuickSchedule={onQuickSchedule}
            onToggle={onToggle}
            onEdit={onEdit}
          />
        ))}
      </div>
    </div>
  );
};

// 五格而不是四格：'none' 那一格必须在。nil 是绝大多数任务的状态，没有它
// 这个视图打开就是四个空格子（服务端 PlanQuery#priority_groups 有同一段论证）。
const PriorityGrid: React.FC<PriorityGridProps> = ({
  groups,
  onDropTask,
  onQuickSchedule,
  onToggle,
  onEdit,
}) => (
  <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
    {groups.map((group) => (
      <PriorityCell
        key={group.key}
        group={group}
        onDropTask={onDropTask}
        onQuickSchedule={onQuickSchedule}
        onToggle={onToggle}
        onEdit={onEdit}
      />
    ))}
  </div>
);

export default PriorityGrid;
